'use client';

import { useCallback, useEffect, useState } from 'react';
import { useAnimation } from 'framer-motion';
import { AVATAR_TRANSITION_DURATION } from '../constants';

type Opts = {
  /**
   * @param opts.initNextImage An optional string parameter representing the URL of an
   *   image being transitioned in upon hook initialization.
   **/
  initNextImage?: string | null;

  /**
   * @param opts.sourceImage A string representing the URL of the image being
   *   used as the current avatar.
   **/
  sourceImage?: string | null;

  /**
   * @param opts.delayTransitionSeconds A number or a callback returning a number
   *    representing the amount of time (in seconds) the transition between avatars
   *    should be delayed. The callback provides `incomingSrc` as an argument, which
   *    is the URL of the incoming image.
   */
  delayTransitionSeconds?: number | ((incomingSrc: string | null) => number);
};

export function useAvatarButtonTransition({
  initNextImage,
  sourceImage,
  delayTransitionSeconds,
}: Opts) {
  const [currentImage, setCurrentImage] = useState<string | null>();
  const [nextImage, setNextImage] = useState<string | null>(initNextImage);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const animationControls = useAnimation();

  const handleImageChange = useCallback(
    async function triggerTransition(img?: string | null) {
      async function animate() {
        animationControls.set({ opacity: 0 });

        setIsTransitioning(true);

        await animationControls.start({
          opacity: 1,
          transition: {
            duration: AVATAR_TRANSITION_DURATION,
            delay:
              typeof delayTransitionSeconds === 'number'
                ? delayTransitionSeconds
                : delayTransitionSeconds?.(nextImage),
          },
        });

        setIsTransitioning(false);
      }

      /**
       * @note We check for `blob:` in order to determine if the
       * incoming image is part of an optimistic update. There is
       * probably a better way to do this.
       **/
      if (!img || img.startsWith('blob:')) {
        setNextImage(img);
        animate();
      } else {
        setCurrentImage(img);
      }
    },
    [animationControls, delayTransitionSeconds, nextImage]
  );

  useEffect(() => {
    if (!isTransitioning) {
      setCurrentImage(sourceImage);
    }
  }, [sourceImage, isTransitioning]);

  return {
    animationControls,
    currentImage,
    handleImageChange,
    isTransitioning,
    nextImage,
  };
}
